/**
 * US Citrus Nursery - Complete Tree Database
 * 41 citrus tree varieties with tags for quiz matching
 * 
 * Tags match Shopify collection filters:
 * - Aromatic Variety (5)
 * - Medicinal Variety (2)
 * - Ornamental Variety (10)
 * - Seedless Variety (20)
 * - Sour Variety (18)
 * - Sweet Variety (32)
 * 
 * Additional quiz-specific tags:
 * - Indoor-Patio (suitable for container growing)
 * - In-Ground (needs warm climate)
 * - Beginner (easy to grow)
 * - Advanced (requires experience)
 * - Cold-Hardy (survives light freezes)
 * - Exotic (unique/rare varieties)
 * - Juice (good for juicing)
 * - Snack (easy peel, eat fresh)
 * - Cook (cooking, zesting, garnishes)
 */

export interface CitrusTree {
  id: string;
  name: string;
  slug: string;
  price: number;
  imageUrl: string;
  productUrl: string;
  reviews: number;
  rating?: number;
  isAvailable?: boolean;
  shortDescription: string;
  tags: string[];
  // Quiz-specific metadata
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  growingLocation: ('indoor' | 'patio' | 'in-ground')[];
  flavorProfile: ('sweet' | 'sour' | 'balanced')[];
  primaryUse: ('snack' | 'juice' | 'cook' | 'aromatic' | 'ornamental')[];
  personality: ('classic' | 'exotic' | 'healer' | 'aesthete')[];
  videoLink?: string;
  // New preference flags
  edibleRind: boolean;  // true for kumquats and similar (eat the whole fruit)
  culturalSignificance?: string;  // optional - describes cultural/historical importance
}

export const citrusTrees: CitrusTree[] = [
  // ===== LEMONS =====
  {
    id: 'meyer-lemon',
    name: 'Meyer Lemon Tree',
    slug: 'meyer-lemon-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260108_123357.jpg?v=1768229478',
    productUrl: 'https://uscitrusnursery.com/products/meyer-lemon-tree',
    reviews: 277,
    shortDescription: 'The undisputed king of indoor citrus. Sweet-tart flavor, fragrant blossoms.',
    tags: ['Sweet Variety', 'Aromatic Variety', 'Seedless Variety', 'Indoor-Patio', 'Beginner', 'Cold-Hardy'],
    difficulty: 'beginner',
    growingLocation: ['indoor', 'patio', 'in-ground'],
    flavorProfile: ['balanced'],
    primaryUse: ['cook', 'juice', 'aromatic'],
    personality: ['classic', 'aesthete'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171749822?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
    culturalSignificance: 'Named after Frank Meyer who brought it from China in 1908. Beloved American classic.'
  },
  {
    id: 'eureka-lemon',
    name: 'Eureka Lemon Tree',
    slug: 'eureka-lemon-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260128_145205.jpg?v=1769711056',
    productUrl: 'https://uscitrusnursery.com/products/eureka-lemon-tree',
    reviews: 55,
    shortDescription: 'Classic grocery store lemon. True tart flavor for baking and cooking.',
    tags: ['Sour Variety', 'Seedless Variety', 'Indoor-Patio'],
    difficulty: 'beginner',
    growingLocation: ['indoor', 'patio', 'in-ground'],
    flavorProfile: ['sour'],
    primaryUse: ['cook', 'juice'],
    personality: ['classic'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171746208?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
  },
  {
    id: 'pink-variegated-lemon',
    name: 'Pink Variegated Eureka Lemon Tree',
    slug: 'pink-variegated-eureka-lemon-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260202_141722.jpg?v=1770327166',
    productUrl: 'https://uscitrusnursery.com/products/pink-variegated-eureka-lemon-tree',
    reviews: 71,
    shortDescription: 'Stunning striped leaves with pink flesh. A showstopper ornamental.',
    tags: ['Ornamental Variety', 'Sour Variety', 'Indoor-Patio'],
    difficulty: 'intermediate',
    growingLocation: ['indoor', 'patio'],
    flavorProfile: ['sour'],
    primaryUse: ['ornamental', 'cook'],
    personality: ['aesthete', 'exotic'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171753888?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963'
  },
  {
    id: 'new-zealand-lemonade',
    name: 'New Zealand Lemonade Tree',
    slug: 'new-zealand-lemonade-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/03_new_zealand_lemoade_2882c6c3-8498-4a93-aebf-177c0047457c.heic?v=1739858534',
    productUrl: 'https://uscitrusnursery.com/products/new-zealand-lemonade-tree',
    reviews: 1,
    shortDescription: 'Sweet lemon you can eat like an orange. Unique and refreshing.',
    tags: ['Sweet Variety', 'Seedless Variety', 'Indoor-Patio'],
    difficulty: 'intermediate',
    growingLocation: ['indoor', 'patio', 'in-ground'],
    flavorProfile: ['sweet', 'balanced'],
    primaryUse: ['snack', 'juice'],
    personality: ['exotic'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171751648?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
  },

  // ===== LIMES =====
  {
    id: 'key-lime',
    name: 'Thornless Mexican (Key) Lime Tree',
    slug: 'thornless-mexican-key-lime-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260108_112934.jpg?v=1768246530',
    productUrl: 'https://uscitrusnursery.com/products/thornless-mexican-key-lime-tree',
    reviews: 76,
    shortDescription: 'The famous Key lime for pies. Intense aromatic flavor, thornless variety.',
    tags: ['Sour Variety', 'Aromatic Variety', 'Indoor-Patio', 'Beginner'],
    difficulty: 'beginner',
    growingLocation: ['indoor', 'patio', 'in-ground'],
    flavorProfile: ['sour'],
    primaryUse: ['cook', 'aromatic'],
    personality: ['classic', 'healer'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171757002?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
    culturalSignificance: 'The iconic Key Lime Pie was born in the Florida Keys. A true American culinary treasure.'
  },
  {
    id: 'persian-lime',
    name: 'Persian (Bearss) Lime Tree',
    slug: 'persian-lime-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260116_090332_2.jpg?v=1769710818',
    productUrl: 'https://uscitrusnursery.com/products/persian-lime-tree',
    reviews: 168,
    shortDescription: 'Seedless grocery store lime. Perfect for drinks and cooking.',
    tags: ['Sour Variety', 'Seedless Variety', 'Indoor-Patio', 'Beginner'],
    difficulty: 'beginner',
    growingLocation: ['indoor', 'patio', 'in-ground'],
    flavorProfile: ['sour'],
    primaryUse: ['cook', 'juice'],
    personality: ['classic'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171756696?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
  },
  {
    id: 'kaffir-lime',
    name: 'Kaffir (Makrut) Lime Tree',
    slug: 'kaffir-makrut-lime-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260202_132750.jpg?v=1770327629',
    productUrl: 'https://uscitrusnursery.com/products/kaffir-makrut-lime-tree',
    reviews: 52,
    shortDescription: 'Grown for the leaves, not the fruit. Essential for Thai curry.',
    tags: ['Medicinal Variety', 'Aromatic Variety', 'Indoor-Patio'],
    difficulty: 'intermediate',
    growingLocation: ['indoor', 'patio'],
    flavorProfile: ['sour'],
    primaryUse: ['cook', 'aromatic'],
    personality: ['healer', 'exotic'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171748290?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
    culturalSignificance: 'Essential in Southeast Asian cuisine. The leaves are sacred in Thai cooking for curries and soups.'
  },
  {
    id: 'australian-finger-lime',
    name: 'Australian Finger Lime Tree',
    slug: 'australian-finger-lime-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260113_113417.jpg?v=1768407174',
    productUrl: 'https://uscitrusnursery.com/products/australian-finger-lime-tree',
    reviews: 169,
    shortDescription: '"Citrus Caviar" - tiny pearls of lime that pop. Chef\'s secret weapon.',
    tags: ['Sour Variety', 'Exotic', 'Indoor-Patio', 'Advanced'],
    difficulty: 'advanced',
    growingLocation: ['indoor', 'patio'],
    flavorProfile: ['sour'],
    primaryUse: ['cook'],
    personality: ['exotic', 'aesthete'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171742885?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963', 
    culturalSignificance: 'Indigenous Australian fruit used by Aboriginal peoples for thousands of years. Now a high-end chef favorite.'
  },
  {
    id: 'rangpur-lime',
    name: 'Rangpur Lime Tree',
    slug: 'rangpur-lime-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260128_133620.jpg?v=1769710765',
    productUrl: 'https://uscitrusnursery.com/products/rangpur-lime-tree',
    reviews: 13,
    shortDescription: 'Orange-colored lime with intense sour flavor. Cold hardy hybrid.',
    tags: ['Sour Variety', 'Cold-Hardy', 'Indoor-Patio'],
    difficulty: 'intermediate',
    growingLocation: ['indoor', 'patio', 'in-ground'],
    flavorProfile: ['sour'],
    primaryUse: ['cook', 'juice'],
    personality: ['exotic'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171754475?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
    culturalSignificance: 'Named after Rangpur, Bangladesh. Popular in Indian cuisine and traditional beverages.'
  },
  {
    id: 'palestine-sweet-lime',
    name: 'Palestine Sweet Lime Tree',
    slug: 'palestine-sweet-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260202_143320.jpg?v=1770327014',
    productUrl: 'https://uscitrusnursery.com/products/palestine-sweet-tree',
    reviews: 0,
    shortDescription: 'Non-acidic lime with sweet, mild flavor. Very unique taste.',
    tags: ['Sweet Variety', 'Seedless Variety', 'Indoor-Patio'],
    difficulty: 'intermediate',
    growingLocation: ['indoor', 'patio'],
    flavorProfile: ['sweet'],
    primaryUse: ['snack', 'juice'],
    personality: ['exotic'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171753060?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
    culturalSignificance: 'Traditional Middle Eastern variety enjoyed fresh as a refreshing drink.'
  },

  // ===== ORANGES =====
  {
    id: 'valencia-orange',
    name: 'Valencia Orange Tree',
    slug: 'valenica-orange-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/da6fa567-517d-45af-8bca-b71618bdc870.jpg?v=1768245926',
    productUrl: 'https://uscitrusnursery.com/products/valencia-orange-tree',
    reviews: 159,
    shortDescription: 'The ultimate juicing orange. Sweet, few seeds, high juice content.',
    tags: ['Sweet Variety', 'Juice', 'In-Ground'],
    difficulty: 'intermediate',
    growingLocation: ['in-ground'],
    flavorProfile: ['sweet'],
    primaryUse: ['juice'],
    personality: ['classic'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171757351?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
    culturalSignificance: 'Named after Valencia, Spain. The world standard for orange juice since the 1800s.'
  },
  {
    id: 'cara-cara-navel',
    name: 'Cara Cara Navel Orange Tree',
    slug: 'cara-cara-navel-orange-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260114_100016.jpg?v=1768406985',
    productUrl: 'https://uscitrusnursery.com/products/cara-cara-navel-orange-tree',
    reviews: 55,
    shortDescription: 'Pink flesh with berry notes. The gourmet navel orange.',
    tags: ['Sweet Variety', 'Seedless Variety', 'In-Ground'],
    difficulty: 'intermediate',
    growingLocation: ['in-ground'],
    flavorProfile: ['sweet'],
    primaryUse: ['snack', 'juice'],
    personality: ['exotic', 'aesthete'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171757752?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
  },
  {
    id: 'n33-navel',
    name: 'N-33 Navel Orange Tree',
    slug: 'n-33-navel-orange-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260205_135127.jpg?v=1770325787',
    productUrl: 'https://uscitrusnursery.com/products/n-33-navel-orange-tree',
    reviews: 0,
    shortDescription: 'Classic seedless navel orange. Sweet eating orange.',
    tags: ['Sweet Variety', 'Seedless Variety', 'In-Ground'],
    difficulty: 'intermediate',
    growingLocation: ['in-ground'],
    flavorProfile: ['sweet'],
    primaryUse: ['snack'],
    personality: ['classic'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171750909?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
  },
  {
    id: 'pineapple-orange',
    name: 'Pineapple Orange Tree',
    slug: 'pineapple-orange-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260203_135906.jpg?v=1770325885',
    productUrl: 'https://uscitrusnursery.com/products/pineapple-orange-tree',
    reviews: 0,
    shortDescription: 'Rich, sweet flavor with hints of pineapple. Heirloom variety.',
    tags: ['Sweet Variety', 'In-Ground'],
    difficulty: 'intermediate',
    growingLocation: ['in-ground'],
    flavorProfile: ['sweet'],
    primaryUse: ['juice', 'snack'],
    personality: ['classic'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171753533?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
  },
  {
    id: 'marrs-orange',
    name: 'Marrs Orange Tree',
    slug: 'marrs-orange-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260205_131905.jpg?v=1770326058',
    productUrl: 'https://uscitrusnursery.com/products/marrs-orange-tree',
    reviews: 0,
    shortDescription: 'Early ripening Texas orange. Sweet with few seeds.',
    tags: ['Sweet Variety', 'Cold-Hardy', 'In-Ground'],
    difficulty: 'intermediate',
    growingLocation: ['in-ground'],
    flavorProfile: ['sweet'],
    primaryUse: ['juice', 'snack'],
    personality: ['classic'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171749129?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
    culturalSignificance: 'Texas heritage variety developed for the Rio Grande Valley climate.'
  },

  // ===== BLOOD ORANGES =====
  {
    id: 'tarocco-blood',
    name: 'Tarocco Blood Orange Tree',
    slug: 'tarocco-blood-orange-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260112_130941.jpg?v=1768246025',
    productUrl: 'https://uscitrusnursery.com/products/tarocco-blood-orange-tree',
    reviews: 114,
    shortDescription: 'Sweetest blood orange with raspberry notes. Stunning red flesh.',
    tags: ['Sweet Variety', 'Seedless Variety', 'In-Ground'],
    difficulty: 'intermediate',
    growingLocation: ['in-ground'],
    flavorProfile: ['sweet'],
    primaryUse: ['snack', 'juice'],
    personality: ['exotic', 'aesthete'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171756276?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
    culturalSignificance: 'Prized Italian variety from Sicily. The red color symbolizes good fortune in Mediterranean cultures.'
  },
  {
    id: 'moro-blood',
    name: 'Moro Blood Orange Tree',
    slug: 'moro-blood-orange-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260205_135825.jpg?v=1770326200',
    productUrl: 'https://uscitrusnursery.com/products/moro-blood-orange-tree',
    reviews: 0,
    shortDescription: 'Darkest blood orange color. Berry and raspberry flavor notes.',
    tags: ['Sweet Variety', 'In-Ground'],
    difficulty: 'intermediate',
    growingLocation: ['in-ground'],
    flavorProfile: ['sweet', 'balanced'],
    primaryUse: ['snack', 'juice'],
    personality: ['exotic', 'aesthete'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171750171?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
    culturalSignificance: 'Sicilian heirloom with the deepest pigmentation of all blood oranges.'
  },
  {
    id: 'sanguinelli-blood',
    name: 'Sanguinelli Blood Orange Tree',
    slug: 'sanguinelli-blood-orange-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260203_165809.jpg?v=1770326433',
    productUrl: 'https://uscitrusnursery.com/products/sanguinelli-blood-orange-tree',
    reviews: 0,
    shortDescription: 'Spanish blood orange with streaked red flesh. Sweet-tart flavor.',
    tags: ['Sweet Variety', 'In-Ground'],
    difficulty: 'intermediate',
    growingLocation: ['in-ground'],
    flavorProfile: ['balanced'],
    primaryUse: ['snack', 'juice'],
    personality: ['exotic'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171755396?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
    culturalSignificance: 'Spanish variety treasured in Mediterranean cuisine.'
  },

  // ===== GRAPEFRUITS =====
  {
    id: 'rio-red-grapefruit',
    name: 'Rio Red Grapefruit Tree',
    slug: 'rio-red-grapefruit-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/new_rio_red.jpg?v=1768326674',
    productUrl: 'https://uscitrusnursery.com/products/rio-red-grapefruit-tree',
    reviews: 112,
    shortDescription: 'The Texas Legend. Sweetest grapefruit with deep red flesh.',
    tags: ['Sweet Variety', 'Seedless Variety', 'In-Ground'],
    difficulty: 'intermediate',
    growingLocation: ['in-ground'],
    flavorProfile: ['sweet', 'balanced'],
    primaryUse: ['juice', 'snack'],
    personality: ['classic'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171754840?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
    culturalSignificance: 'Texas state fruit. Developed at Texas A&M, a symbol of Rio Grande Valley agriculture.'
  },
  {
    id: 'duncan-grapefruit',
    name: 'Duncan Grapefruit Tree',
    slug: 'duncan-grapefruit-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/37_duncan_grapefruit_1.heic?v=1759944078',
    productUrl: 'https://uscitrusnursery.com/products/duncan-grapefruit-tree',
    reviews: 0,
    shortDescription: 'White flesh grapefruit. Classic tart flavor, very productive.',
    tags: ['Sour Variety', 'In-Ground'],
    difficulty: 'intermediate',
    growingLocation: ['in-ground'],
    flavorProfile: ['sour', 'balanced'],
    primaryUse: ['juice'],
    personality: ['classic'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171745394?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
    culturalSignificance: 'One of the oldest grapefruit varieties in America, dating back to 1830 Florida.'
  },
  {
    id: 'oroblanco-grapefruit',
    name: 'Oroblanco (Pomelo) Grapefruit Tree',
    slug: 'oroblanco-grapefruit-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/45_oroblanco_hybrid_grapefruit_1.heic?v=1753711592',
    productUrl: 'https://uscitrusnursery.com/products/oroblanco-grapefruit-tree',
    reviews: 0,
    shortDescription: 'Grapefruit-pomelo hybrid. Sweet with no bitterness.',
    tags: ['Sweet Variety', 'Seedless Variety', 'In-Ground'],
    difficulty: 'intermediate',
    growingLocation: ['in-ground'],
    flavorProfile: ['sweet'],
    primaryUse: ['snack', 'juice'],
    personality: ['exotic'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171752307?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
  },

  // ===== POMELOS =====
  {
    id: 'chandler-pomelo',
    name: 'Chandler Pomelo Tree',
    slug: 'chandler-pomelo-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/01_pummelo_chandler.heic?v=1742685287',
    productUrl: 'https://uscitrusnursery.com/products/chandler-pomelo-tree',
    reviews: 23,
    shortDescription: 'Giant sweet citrus. Pink flesh, mild flavor, impressive size.',
    tags: ['Sweet Variety', 'Seedless Variety', 'In-Ground'],
    difficulty: 'intermediate',
    growingLocation: ['in-ground'],
    flavorProfile: ['sweet'],
    primaryUse: ['snack'],
    personality: ['exotic'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171744340?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
    culturalSignificance: 'Traditional Chinese New Year fruit symbolizing prosperity and good fortune.'
  },
  {
    id: 'hirado-buntan-pomelo',
    name: 'Hirado Buntan Pomelo Tree',
    slug: 'pomelo-hirado-buntan-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/114_pomelo_hirado_buntan.heic?v=1753715369',
    productUrl: 'https://uscitrusnursery.com/products/pomelo-hirado-buntan-tree',
    reviews: 0,
    shortDescription: 'Japanese pomelo variety. Very sweet, easy to peel.',
    tags: ['Sweet Variety', 'In-Ground'],
    difficulty: 'intermediate',
    growingLocation: ['in-ground'],
    flavorProfile: ['sweet'],
    primaryUse: ['snack'],
    personality: ['exotic'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171747978?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
    culturalSignificance: 'Named after Hirado Island, Japan. A treasured variety in Japanese citrus culture.'
  },

  // ===== MANDARINS & TANGERINES =====
  {
    id: 'kishu-mandarin',
    name: 'Kishu Mandarin Tree',
    slug: 'kishu-mandarin-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260107_111305.jpg?v=1767815298',
    productUrl: 'https://uscitrusnursery.com/products/kishu-mandarin-tree',
    reviews: 87,
    shortDescription: '"The Candy of Citrus" - tiny, seedless, incredibly sweet.',
    tags: ['Sweet Variety', 'Seedless Variety', 'Indoor-Patio', 'Beginner'],
    difficulty: 'beginner',
    growingLocation: ['indoor', 'patio', 'in-ground'],
    flavorProfile: ['sweet'],
    primaryUse: ['snack'],
    personality: ['classic'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171748664?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
    culturalSignificance: 'Ancient Japanese variety from Wakayama Prefecture, treasured for centuries.'
  },
  {
    id: 'frost-owari-satsuma',
    name: 'Frost Owari Satsuma Mandarin Tree',
    slug: 'frost-owari-satsuma-mandarin',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/104_frost_owari_satsuma_mandarin.heic?v=1753717693',
    productUrl: 'https://uscitrusnursery.com/products/frost-owari-satsuma-mandarin',
    reviews: 4,
    shortDescription: 'Cold hardy mandarin. Easy peel, seedless, sweet flavor.',
    tags: ['Sweet Variety', 'Seedless Variety', 'Cold-Hardy', 'Indoor-Patio', 'Beginner'],
    difficulty: 'beginner',
    growingLocation: ['indoor', 'patio', 'in-ground'],
    flavorProfile: ['sweet'],
    primaryUse: ['snack'],
    personality: ['classic'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171747186?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
    culturalSignificance: 'Named after Owari Province, Japan. First citrus brought to America from Japan in the 1870s.'
  },
  {
    id: 'dobashi-beni-satsuma',
    name: 'Dobashi Beni Satsuma Mandarin Tree',
    slug: 'dobashi-beni-satsuma-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/125_mnd_dobashi_beni_sat_2.png?v=1759866966',
    productUrl: 'https://uscitrusnursery.com/products/dobashi-beni-satsuma-tree',
    reviews: 0,
    shortDescription: 'Deep orange-red flesh satsuma. Extra sweet, early ripening.',
    tags: ['Sweet Variety', 'Seedless Variety', 'Cold-Hardy', 'Indoor-Patio'],
    difficulty: 'beginner',
    growingLocation: ['indoor', 'patio', 'in-ground'],
    flavorProfile: ['sweet'],
    primaryUse: ['snack'],
    personality: ['exotic'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171745076?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
    culturalSignificance: 'Premium Japanese variety known for exceptional color and sweetness.'
  },
  {
    id: 'shiranui-mandarin',
    name: 'Shiranui Mandarin Tree',
    slug: 'shiranui-mandarin-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/61_cmt_shiranui_b249eb0d-8aee-4da8-8e4e-4b815b510843.heic?v=1739857157',
    productUrl: 'https://uscitrusnursery.com/products/shiranui-mandarin-tree',
    reviews: 0,
    shortDescription: 'Also known as Sumo. Large, sweet, easy-peel mandarin.',
    tags: ['Sweet Variety', 'Seedless Variety', 'In-Ground'],
    difficulty: 'intermediate',
    growingLocation: ['in-ground'],
    flavorProfile: ['sweet'],
    primaryUse: ['snack'],
    personality: ['exotic'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171755951?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
    culturalSignificance: 'Japanese hybrid developed in 1972. Named after a Shinto shrine, meaning "white mystery."'
  },
  {
    id: 'murcott-mandarin',
    name: 'Murcott W. Afourer Mandarin Tree',
    slug: 'murcott-w-afourer-mandarin-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/63_w_murcott_afourer_mnd.heic?v=1759943905',
    productUrl: 'https://uscitrusnursery.com/products/murcott-w-afourer-mandarin-tree',
    reviews: 0,
    shortDescription: 'Honey mandarin. Extremely sweet, late season harvest.',
    tags: ['Sweet Variety', 'Seedless Variety', 'In-Ground'],
    difficulty: 'intermediate',
    growingLocation: ['in-ground'],
    flavorProfile: ['sweet'],
    primaryUse: ['snack'],
    personality: ['classic'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171750502?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
  },
  {
    id: 'cleopatra-mandarin',
    name: 'Cleopatra Mandarin Tree',
    slug: 'cleopatra-mandarin-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260205_141306.jpg?v=1770326896',
    productUrl: 'https://uscitrusnursery.com/products/cleopatra-mandarin-tree',
    reviews: 0,
    shortDescription: 'Ancient variety with rich sweet flavor. Very ornamental.',
    tags: ['Sweet Variety', 'Ornamental Variety', 'In-Ground'],
    difficulty: 'intermediate',
    growingLocation: ['in-ground'],
    flavorProfile: ['sweet'],
    primaryUse: ['snack', 'ornamental'],
    personality: ['exotic', 'aesthete'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171744682?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
    culturalSignificance: 'One of the oldest known mandarin varieties, cultivated for thousands of years in India.'
  },
  {
    id: 'ponkan-mandarin',
    name: 'Ponkan Mandarin Tree',
    slug: 'ponkan-mandarin-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260205_140440.jpg?v=1770326748',
    productUrl: 'https://uscitrusnursery.com/products/ponkan-mandarin-tree',
    reviews: 0,
    shortDescription: 'Large Chinese mandarin. Very sweet, aromatic, upright grower.',
    tags: ['Sweet Variety', 'Aromatic Variety', 'In-Ground'],
    difficulty: 'intermediate',
    growingLocation: ['in-ground'],
    flavorProfile: ['sweet'],
    primaryUse: ['snack'],
    personality: ['classic'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171754218?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
    culturalSignificance: 'Traditional Chinese New Year fruit. Name means "lucky" in Chinese, given as gifts for prosperity.'
  },
  {
    id: 'page-mandarin',
    name: 'Page Mandarin Tree',
    slug: 'page-mandarin-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/01_mnd_page.heic?v=1742685969',
    productUrl: 'https://uscitrusnursery.com/products/page-mandarin-tree',
    reviews: 0,
    shortDescription: 'Rich, complex flavor mandarin. Very juicy, few seeds.',
    tags: ['Sweet Variety', 'Seedless Variety', 'In-Ground'],
    difficulty: 'intermediate',
    growingLocation: ['in-ground'],
    flavorProfile: ['sweet'],
    primaryUse: ['snack', 'juice'],
    personality: ['classic'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171752602?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
  },

  // ===== TANGELOS =====
  {
    id: 'orlando-tangelo',
    name: 'Orlando Tangelo Tree',
    slug: 'orlando-tangelo-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/112_tangelo_orlando.heic?v=1759943971',
    productUrl: 'https://uscitrusnursery.com/products/orlando-tangelo-tree',
    reviews: 0,
    shortDescription: 'Tangerine-grapefruit hybrid. Sweet-tart, very juicy.',
    tags: ['Sweet Variety', 'Seedless Variety', 'In-Ground'],
    difficulty: 'intermediate',
    growingLocation: ['in-ground'],
    flavorProfile: ['balanced'],
    primaryUse: ['juice', 'snack'],
    personality: ['exotic'],
    edibleRind: false,
  },

  // ===== KUMQUATS (Edible Rind!) =====
  {
    id: 'nagami-kumquat',
    name: 'Nagami Kumquat Tree',
    slug: 'nagami-kumquat-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/22_kumquat_magami_cbfc6bc0-d64a-4e45-8fdf-4baaf8f273da.heic?v=1739857965',
    productUrl: 'https://uscitrusnursery.com/products/nagami-kumquat-tree',
    reviews: 92,
    shortDescription: 'Eat the whole thing! Sweet skin, sour inside. Very cold hardy.',
    tags: ['Sweet Variety', 'Sour Variety', 'Ornamental Variety', 'Cold-Hardy', 'Indoor-Patio', 'Beginner'],
    difficulty: 'beginner',
    growingLocation: ['indoor', 'patio', 'in-ground'],
    flavorProfile: ['balanced'],
    primaryUse: ['snack', 'ornamental'],
    personality: ['aesthete', 'classic'],
    edibleRind: true,
    videoLink: 'https://player.vimeo.com/video/1171751281?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
    culturalSignificance: 'Traditional Chinese symbol of good luck. Given as gifts during Lunar New Year.'
  },
  {
    id: 'meiwa-kumquat',
    name: 'Meiwa Kumquat Tree',
    slug: 'meiwa-kumquat-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260128_142126.jpg?v=1769710884',
    productUrl: 'https://uscitrusnursery.com/products/meiwa-kumquat-tree',
    reviews: 0,
    shortDescription: 'The snacking kumquat. Sweet skin AND sweet flesh.',
    tags: ['Sweet Variety', 'Ornamental Variety', 'Cold-Hardy', 'Indoor-Patio', 'Beginner'],
    difficulty: 'beginner',
    growingLocation: ['indoor', 'patio', 'in-ground'],
    flavorProfile: ['sweet'],
    primaryUse: ['snack', 'ornamental'],
    personality: ['aesthete', 'classic'],
    edibleRind: true,
    videoLink: 'https://player.vimeo.com/video/1171749504?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
    culturalSignificance: 'Japanese variety meaning "clear river" - prized as the sweetest kumquat.'
  },
  {
    id: 'fukushu-kumquat',
    name: 'Fukushu Kumquat Tree',
    slug: 'fukushu-kumquat-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/94_kumquat_fukushu_1.heic?v=1759943809',
    productUrl: 'https://uscitrusnursery.com/products/fukushu-kumquat-tree',
    reviews: 0,
    shortDescription: 'Bell-shaped kumquat. Sweeter than Nagami, great ornamental.',
    tags: ['Sweet Variety', 'Ornamental Variety', 'Cold-Hardy', 'Indoor-Patio'],
    difficulty: 'beginner',
    growingLocation: ['indoor', 'patio', 'in-ground'],
    flavorProfile: ['sweet', 'balanced'],
    primaryUse: ['snack', 'ornamental'],
    personality: ['aesthete'],
    edibleRind: true,
    videoLink: 'https://player.vimeo.com/video/1171747651?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
    culturalSignificance: 'Named after Fukushu (Fuzhou), China, where kumquats have been cultivated for centuries.'
  },
  {
    id: 'eustis-kumquat',
    name: 'Eustis Kumquat Tree',
    slug: 'eustis-kumquat-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/86_kumquat_eustis.heic?v=1753719109',
    productUrl: 'https://uscitrusnursery.com/products/eustis-kumquat-tree',
    reviews: 0,
    shortDescription: 'Limequat hybrid. Lime flavor in a kumquat-sized package.',
    tags: ['Sour Variety', 'Cold-Hardy', 'Indoor-Patio'],
    difficulty: 'beginner',
    growingLocation: ['indoor', 'patio', 'in-ground'],
    flavorProfile: ['sour'],
    primaryUse: ['cook', 'snack'],
    personality: ['exotic'],
    edibleRind: true,
    videoLink: 'https://player.vimeo.com/video/1171746751?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
  },
  {
    id: 'nippon-orangequat',
    name: 'Nippon Orangequat Tree',
    slug: 'nippon-orangequat-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/110_nippon.heic?v=1753719275',
    productUrl: 'https://uscitrusnursery.com/products/nippon-orangequat-tree',
    reviews: 0,
    shortDescription: 'Kumquat-orange hybrid. Sweet, bright orange color.',
    tags: ['Sweet Variety', 'Cold-Hardy', 'Indoor-Patio'],
    difficulty: 'beginner',
    growingLocation: ['indoor', 'patio', 'in-ground'],
    flavorProfile: ['sweet'],
    primaryUse: ['snack', 'ornamental'],
    personality: ['exotic', 'aesthete'],
    edibleRind: true,
    videoLink: 'https://player.vimeo.com/video/1171752027?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
  },

  // ===== CALAMONDIN =====
  {
    id: 'calamondin',
    name: 'Calamondin Tree',
    slug: 'calamondin-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260203_134006_2.jpg?v=1770327281',
    productUrl: 'https://uscitrusnursery.com/products/calamondin-tree',
    reviews: 49,
    shortDescription: 'Bulletproof houseplant. Sour fruit perfect for drinks and Filipino cuisine.',
    tags: ['Sour Variety', 'Ornamental Variety', 'Indoor-Patio', 'Beginner'],
    difficulty: 'beginner',
    growingLocation: ['indoor', 'patio'],
    flavorProfile: ['sour'],
    primaryUse: ['cook', 'ornamental'],
    personality: ['classic', 'aesthete'],
    edibleRind: true,
    videoLink: 'https://player.vimeo.com/video/1171743473?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
    culturalSignificance: 'The Philippine national citrus. Essential in Filipino cuisine for sawsawan (dipping sauces).'
  },

  // ===== CITRONS =====
  {
    id: 'etrog-citron',
    name: 'Etrog Citron Tree',
    slug: 'etrog-citron-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260202_131243_2.jpg?v=1770327097',
    productUrl: 'https://uscitrusnursery.com/products/etrog-citron-tree',
    reviews: 4,
    shortDescription: 'Ancient citrus used in Jewish ceremonies. Fragrant, thick rind.',
    tags: ['Aromatic Variety', 'Medicinal Variety', 'Indoor-Patio'],
    difficulty: 'intermediate',
    growingLocation: ['indoor', 'patio'],
    flavorProfile: ['sour'],
    primaryUse: ['aromatic', 'ornamental'],
    personality: ['healer', 'exotic'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171745813?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
    culturalSignificance: 'Sacred in Judaism. Used during Sukkot festival. One of the oldest citrus varieties, mentioned in ancient texts.'
  },
  {
    id: 'buddhas-hand',
    name: "Buddha's Hand Citron Tree",
    slug: 'buddhas-hand-citron-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/Photoroom_20260107_125714.jpg?v=1767814942',
    productUrl: 'https://uscitrusnursery.com/products/buddhas-hand-citron-tree',
    reviews: 0,
    shortDescription: 'All zest, no flesh. Incredible fragrance for vodka, candy, display.',
    tags: ['Aromatic Variety', 'Ornamental Variety', 'Indoor-Patio', 'Advanced'],
    difficulty: 'advanced',
    growingLocation: ['indoor', 'patio'],
    flavorProfile: ['sour'],
    primaryUse: ['aromatic', 'ornamental', 'cook'],
    personality: ['exotic', 'aesthete', 'healer'],
    edibleRind: false,
    culturalSignificance: 'Buddhist symbol of good fortune. Placed on temple altars and given as religious offerings across Asia.'
  },

  // ===== YUZU =====
  {
    id: 'yuzu',
    name: 'Yuzu Ichandrin Tree',
    slug: 'yuzu-tree',
    price: 79.95,
    imageUrl: 'https://cdn.shopify.com/s/files/1/0571/1167/5030/files/127_yuzu_ichandrin_2.png?v=1759867496',
    productUrl: 'https://uscitrusnursery.com/products/yuzu-tree',
    reviews: 10,
    shortDescription: 'Japanese cooking essential. Cold hardy, incredibly aromatic.',
    tags: ['Sour Variety', 'Aromatic Variety', 'Cold-Hardy', 'Indoor-Patio', 'Advanced'],
    difficulty: 'advanced',
    growingLocation: ['indoor', 'patio', 'in-ground'],
    flavorProfile: ['sour'],
    primaryUse: ['cook', 'aromatic'],
    personality: ['exotic', 'healer'],
    edibleRind: false,
    videoLink: 'https://player.vimeo.com/video/1171758237?title=0&byline=0&portrait=0&muted=1&autoplay=1&autopause=0&loop=1&app_id=122963',
    culturalSignificance: 'Essential in Japanese cuisine. Yuzu baths (yuzuyu) are a winter solstice tradition dating back centuries.'
  },
  {
    id: 'dancy-mandarin',
    name: 'Dancy Mandarin Tree',
    slug: 'dancy-mandarin-tree',
    price: 79.95,
    imageUrl: '',
    productUrl: 'https://uscitrusnursery.com/products/dancy-mandarin-tree',
    reviews: 0,
    shortDescription: "America's original Christmas tangerine. Bold, sweet, rich flavor.",
    tags: ['Sweet Variety', 'Holiday Citrus', 'Historic Variety', 'Easy-Peel'],
    difficulty: 'beginner',
    growingLocation: ['patio', 'in-ground'],
    flavorProfile: ['sweet', 'balanced'],
    primaryUse: ['snack', 'juice'],
    personality: ['classic'],
    edibleRind: false,
    culturalSignificance: "Known nationwide as the 'Christmas Tangerine' in the late 1800s, shipped in wooden crates across the US every winter."
  }
];

// Helper function to get tree by ID
export const getTreeById = (id: string): CitrusTree | undefined => {
  return citrusTrees.find(tree => tree.id === id);
};

// Helper function to get trees by tag
export const getTreesByTag = (tag: string): CitrusTree[] => {
  return citrusTrees.filter(tree => tree.tags.includes(tag));
};

// Helper function to search trees
export const searchTrees = (query: string): CitrusTree[] => {
  const lowerQuery = query.toLowerCase();
  return citrusTrees.filter(tree => 
    tree.name.toLowerCase().includes(lowerQuery) ||
    tree.shortDescription.toLowerCase().includes(lowerQuery) ||
    tree.tags.some(tag => tag.toLowerCase().includes(lowerQuery))
  );
};
